/**
 * Entidade Reminder (Lembrete / Aviso).
 * Referencia User, Client e opcionalmente Service.
 */

import { z } from 'zod';

export const reminderStatusSchema = z.enum(['sent', 'contacted', 'scheduled', 'cancelled']);
export type ReminderStatus = z.infer<typeof reminderStatusSchema>;

/** Falha ao enviar lembrete ao dono (persistido pelo backend quando aplicável). */
export const reminderSendFailureSchema = z.object({
  at: z.string(),
  message: z.string().nullable().optional(),
});
export type ReminderSendFailure = z.infer<typeof reminderSendFailureSchema>;

/** Normaliza sent_at: aceita array ou (retrocompat) string/null → sempre string[]. */
const sentAtSchema = z.preprocess(
  (val) => {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') return [val];
    return [];
  },
  z.array(z.string())
);

export const reminderSchema = z.object({
  id: z.string().min(1),
  user_id: z.string().min(1),
  client_id: z.string().min(1),
  service_id: z.string().min(1).nullable(),
  expected_date: z.string(),
  equipment_reference: z.string().nullable(),
  status: reminderStatusSchema,
  /** Datas/horas em que o lembrete foi enviado (ex.: 7 dias antes, 1 dia antes, no dia). Lista vazia = ainda não enviado. */
  sent_at: sentAtSchema,
  contacted_at: z.string().nullable(),
  scheduled_date: z.string().nullable(),
  value_charged: z.number().nullable(),
  create_reminder_for_date: z.boolean(),
  /** Tentativas de envio com erro (ex.: WhatsApp). Opcional para documentos antigos. */
  send_failures: z.array(reminderSendFailureSchema).optional().default([]),
  created_at: z.string(),
  updated_at: z.string(),
});

export type Reminder = z.infer<typeof reminderSchema>;

/** Firestore Timestamp, Date ou string ISO. */
function coerceDateLikeToIso(v: unknown): string | undefined {
  if (v === null || v === undefined) return undefined;
  if (typeof v === 'string') {
    const t = v.trim();
    return t.length > 0 ? t : undefined;
  }
  if (v instanceof Date) return v.toISOString();
  if (typeof v === 'object' && v !== null && 'toDate' in v && typeof (v as { toDate: () => Date }).toDate === 'function') {
    return (v as { toDate: () => Date }).toDate().toISOString();
  }
  if (typeof v === 'object' && v !== null && 'seconds' in v && typeof (v as { seconds: number }).seconds === 'number') {
    const s = (v as { seconds: number; nanoseconds?: number }).seconds;
    const ns = (v as { nanoseconds?: number }).nanoseconds ?? 0;
    return new Date(s * 1000 + ns / 1e6).toISOString();
  }
  return undefined;
}

/** DocumentReference ou string (ID). */
function coerceFirestoreId(v: unknown): string | null | undefined {
  if (v === null) return null;
  if (v === undefined) return undefined;
  if (typeof v === 'string') {
    const t = v.trim();
    return t.length > 0 ? t : null;
  }
  if (typeof v === 'object' && v !== null && 'id' in v && typeof (v as { id: string }).id === 'string') {
    return (v as { id: string }).id;
  }
  return undefined;
}

/**
 * Normaliza leitura Firestore antes do `reminderSchema` (Timestamps, refs, legado).
 */
export function normalizeReminderFields(id: string, raw: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...raw, id };

  const dateKeys = [
    'expected_date',
    'contacted_at',
    'scheduled_date',
    'created_at',
    'updated_at',
  ] as const;
  for (const key of dateKeys) {
    const coerced = coerceDateLikeToIso(raw[key]);
    if (coerced !== undefined) {
      out[key] = coerced;
    }
  }

  const sentRaw = raw.sent_at;
  if (Array.isArray(sentRaw)) {
    out.sent_at = sentRaw.map((item) => {
      const iso = coerceDateLikeToIso(item);
      if (iso !== undefined) return iso;
      if (typeof item === 'string') return item;
      return String(item);
    });
  } else if (sentRaw != null) {
    const iso = coerceDateLikeToIso(sentRaw);
    out.sent_at = iso != null ? [iso] : typeof sentRaw === 'string' ? [sentRaw] : [];
  }

  if (raw.service_id === undefined || raw.service_id === null) {
    out.service_id = null;
  } else {
    const sid = coerceFirestoreId(raw.service_id);
    out.service_id = sid === undefined || sid === null ? null : sid;
  }

  const uid = coerceFirestoreId(raw.user_id);
  if (uid != null) {
    out.user_id = uid;
  }

  const cid = coerceFirestoreId(raw.client_id);
  if (cid != null) {
    out.client_id = cid;
  }

  const equip = raw.equipment_reference;
  if (equip === '' || (typeof equip === 'string' && equip.trim() === '')) {
    out.equipment_reference = null;
  }

  const vc = raw.value_charged;
  if (typeof vc === 'number' && Number.isFinite(vc)) {
    out.value_charged = vc;
  } else if (typeof vc === 'string' && vc.trim() !== '') {
    const normalized = vc.replace(/\./g, '').replace(',', '.');
    const n = Number.parseFloat(normalized);
    if (!Number.isNaN(n)) {
      out.value_charged = n;
    }
  }

  if (typeof raw.create_reminder_for_date !== 'boolean') {
    out.create_reminder_for_date = raw.create_reminder_for_date === true;
  }

  const sfRaw = raw.send_failures;
  if (Array.isArray(sfRaw)) {
    out.send_failures = sfRaw
      .map((item) => {
        if (item == null || typeof item !== 'object') return null;
        const o = item as Record<string, unknown>;
        const atIso = coerceDateLikeToIso(o.at) ?? (typeof o.at === 'string' ? o.at.trim() : '');
        if (!atIso) return null;
        const msg = o.message;
        return {
          at: atIso,
          message: typeof msg === 'string' && msg.trim() !== '' ? msg : null,
        };
      })
      .filter((x): x is { at: string; message: string | null } => x != null);
  } else {
    out.send_failures = [];
  }

  return out;
}

/** Referências Firestore da entidade Reminder (paths e strings centralizadas). */
export class ReminderRef {
  static readonly collection = 'reminders';
  static docPath(id: string): string {
    return `${this.collection}/${id}`;
  }
}
